import { Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { MeetingService } from '../../../../../services/meeting.service';
import { UserMeetingPreferencesService } from '../../../../../services/user-meeting-preferences.service';
import { UserService } from '../../../../../services/user.service';
import { Meeting } from '../../../../../models/meeting';
import { MeetingPreferences } from '../../../../../models/meeting-preferences';
import { notePreview, notePreviewTruncated } from '../../../../../helpers/note-preview';
import {
  DenyMeetingDialogComponent,
  DenyMeetingDialogResult,
} from '../../../../common/deny-meeting-dialog/deny-meeting-dialog';
import {
  ProposeChangesDialogComponent,
  ProposeChangesDialogResult,
} from '../../../../common/propose-changes-dialog/propose-changes-dialog';

export type DetailsView = 'details' | 'cancel' | 'canceled' | 'note';

export interface MeetingDetailsDialogData {
  meeting: Meeting;
}

export type MeetingDetailsDialogResult =
  | { action: 'canceled' }
  | { action: 'responded' }
  | undefined;

@Component({
  selector: 'app-meeting-details-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, DatePipe],
  templateUrl: './meeting-details.html',
  styleUrl: './meeting-details.css',
})
export class MeetingDetailsDialogComponent {
  private meetingService = inject(MeetingService);
  private prefsService = inject(UserMeetingPreferencesService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  protected dialogRef = inject(MatDialogRef<MeetingDetailsDialogComponent>);
  protected data = inject<MeetingDetailsDialogData>(MAT_DIALOG_DATA);

  // A signal because submitCancel() sets it from inside an HTTP subscribe
  // callback — under zoneless change detection a plain field assigned there
  // never triggers a re-render.
  protected view = signal<DetailsView>('details');
  protected meeting: Meeting;

  /** The notes row preview — see helpers/note-preview.ts. */
  protected get notePreview(): string {
    return notePreview(this.meeting.note);
  }

  /** Whether notePreview actually cut anything — controls the trailing "…". */
  protected get notePreviewTruncated(): boolean {
    return notePreviewTruncated(this.meeting.note);
  }

  /**
   * True when there's a proposal on this meeting and it's this student's
   * turn to respond to it. False for a proposal the student made themselves
   * (still shown, but read-only bc they're waiting on the counselor instead).
   */
  protected get isAwaitingMyResponse(): boolean {
    return (
      this.meeting.requestedChanges !== null &&
      this.meeting.awaitingResponseUserId === this.userService.currentUser?.id
    );
  }

  protected submitting = signal(false);
  /** Server-side rejection message. Set from an async callback, so it has to be
   * a signal or the message never reaches the screen under zoneless CD. */
  protected submitError = signal<string | null>(null);

  // Fetched from the API for the meeting's counselor; null until loaded.
  private preferences = signal<MeetingPreferences | null>(null);

  /**
   * The counselor's standing meeting instructions — the same text the request
   * dialog shows in its confirmation popup, kept available here afterwards.
   * Read live off their preferences rather than stored on the meeting, so an
   * edit reaches every upcoming booking. Null hides the row.
   */
  protected get counselorInstructions(): string | null {
    return this.preferences()?.instructions ?? null;
  }

  constructor() {
    this.meeting = this.data.meeting;
    this.prefsService.getUserMeetingPreferences(this.meeting.facultyUserId).subscribe({
      next: (prefs) => this.preferences.set(MeetingPreferences.digest(prefs)),
      error: () => this.preferences.set(null),
    });
  }

  /**
   * Navigates to a different view within the dialog.
   * @param v - The view to switch to.
   */
  protected goToView(v: DetailsView): void {
    this.view.set(v);
  }

  /**
   * Opens the same propose-changes dialog the counselor uses. A student
   * requesting a different time/duration/location works exactly the same
   * way as a counselor proposing one, just from the other side.
   */
  protected openProposeChanges(): void {
    this.dialog
      .open(ProposeChangesDialogComponent, {
        data: { meeting: this.meeting },
        width: '720px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: ProposeChangesDialogResult) => {
        if (result?.action === 'proposed') this.dialogRef.close({ action: 'responded' });
      });
  }

  /** Accepts a counselor-proposed change — the meeting adopts the proposed
   *  terms. A still-pending request stays pending either way; only the
   *  counselor's own confirmation ever finalizes it. */
  protected acceptChanges(): void {
    this.submitting.set(true);
    this.submitError.set(null);
    this.meetingService.confirmMeeting(this.meeting.id).subscribe({
      next: () => this.dialogRef.close({ action: 'responded' }),
      error: (err: HttpErrorResponse) => {
        this.submitting.set(false);
        this.submitError.set(err.error?.error || 'Something went wrong. Please try again.');
      },
    });
  }

  /** Declines a counselor proposed change, leaving whatever stood before it. */
  protected declineChanges(): void {
    this.dialog
      .open(DenyMeetingDialogComponent, {
        data: { meeting: this.meeting },
        width: '480px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: DenyMeetingDialogResult) => {
        if (!result) return;
        this.submitting.set(true);
        this.submitError.set(null);
        this.meetingService.denyMeeting(this.meeting.id, result.note).subscribe({
          next: () => this.dialogRef.close({ action: 'responded' }),
          error: (err: HttpErrorResponse) => {
            this.submitting.set(false);
            this.submitError.set(err.error?.error || 'Something went wrong. Please try again.');
          },
        });
      });
  }

  /** Cancels the meeting and swaps to the success view on confirmation. */
  protected submitCancel(): void {
    this.submitting.set(true);
    this.submitError.set(null);
    this.meetingService.cancelMeeting(this.meeting.id).subscribe({
      next: () => {
        this.submitting.set(false);
        this.view.set('canceled');

        // The meeting is already canceled server-side, but the list only
        // learns about it from the close payload. A backdrop or Escape close
        // would otherwise pass undefined and leave the card looking active
        // until a refresh — so take those over and route them through
        // finishCancel().
        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe(() => this.finishCancel());
        this.dialogRef.keydownEvents().subscribe((event) => {
          if (event.key === 'Escape') this.finishCancel();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.submitting.set(false);
        this.submitError.set(
          err.error?.error || 'Something went wrong sending your request. Please try again.',
        );
      },
    });
  }

  /** Dismisses the cancellation confirmation, handing the result back to the list. */
  protected finishCancel(): void {
    this.dialogRef.close({ action: 'canceled' });
  }
}
